import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    employeeV2Service, payrunService, payslipService, employeeService, sessionService,
} from '../../../services/api';
import {
    C, fmtMoney, fmtDate, fmtDateTime,
    cardStyle, cardHeaderStyle, cardTitleStyle, labelStyle, valueStyle, inputStyle,
    primaryButtonStyle, secondaryButtonStyle, linkButtonStyle,
} from './shared';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const STATUS_STYLE = {
    unpaid: { bg: '#fef2f2', color: '#dc2626' },
    partial: { bg: '#fffbeb', color: '#d97706' },
    settled: { bg: C.greenLight, color: C.green },
};
const STATUS_KEY = { unpaid: 'status_unpaid', partial: 'status_partial', settled: 'status_settled' };

const METHOD_KEY = {
    cash_from_till: 'method_cash_from_till',
    cash_from_safe: 'method_cash_from_safe',
    bank_transfer: 'method_bank_transfer',
    check: 'method_check',
    other: 'method_other',
};

const StatusPill = ({ status, t }) => {
    const s = STATUS_STYLE[status] || { bg: '#f1f5f9', color: C.muted };
    return (
        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '3px 9px', background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {t(STATUS_KEY[status] || status || 'unpaid')}
        </span>
    );
};

// ─── Current period card ──────────────────────────────────────────────────────

const CurrentPeriodCard = ({ employee, currency, onBonusChanged, t }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [run, setRun] = useState(null);
    const [lineItem, setLineItem] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const runsBundle = await payrunService.list();
            const candidates = [...(runsBundle.to_run || []), ...(runsBundle.runs || [])]
                .filter(r => r.status !== 'closed');
            const freq = employee.compensation?.pay_frequency;
            const match = candidates.find(r => !freq || r.cadence === freq) || candidates[0];
            if (!match) {
                setRun(null);
                setLineItem(null);
                return;
            }
            const detail = await payrunService.get(match.pay_run_id);
            const li = (detail.line_items || []).find(l => l.employee_id === employee.employee_id);
            setRun(detail);
            setLineItem(li || null);
        } catch {
            setError(t('failed_to_load'));
        } finally {
            setLoading(false);
        }
    }, [employee.employee_id, employee.compensation?.pay_frequency, t]);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div style={cardStyle}>
                <div style={cardHeaderStyle}><h3 style={cardTitleStyle}>{t('current_period')}</h3></div>
                <div style={{ fontSize: 13, color: C.mutedLight, padding: '12px 0' }}>{t('loading')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={cardStyle}>
                <div style={cardHeaderStyle}><h3 style={cardTitleStyle}>{t('current_period')}</h3></div>
                <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '8px 12px', fontSize: 12, color: C.redText }}>{error}</div>
            </div>
        );
    }

    if (!run || !lineItem) {
        return (
            <div style={cardStyle}>
                <div style={cardHeaderStyle}><h3 style={cardTitleStyle}>{t('current_period')}</h3></div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '24px 0', color: C.mutedLight }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <span style={{ fontSize: 13 }}>{t('no_active_run')}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', paddingBottom: 8 }}>
                    <button onClick={() => onBonusChanged('advance')} style={secondaryButtonStyle}>{t('add_advance')}</button>
                </div>
            </div>
        );
    }

    const paidSum = (lineItem.payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const outstanding = Math.max(0, Number(lineItem.net_pay || 0) - paidSum);

    return (
        <div style={cardStyle}>
            <div style={cardHeaderStyle}>
                <h3 style={cardTitleStyle}>{t('current_period')}</h3>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StatusPill status={lineItem.status} t={t} />
                    <button onClick={() => navigate(`/portal/payrun/${run.pay_run_id}`)} style={linkButtonStyle}>
                        {t('view_run')}
                    </button>
                </div>
            </div>

            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
                {fmtDate(run.period_start)} – {fmtDate(run.period_end)} · {t('pay_date')}: {fmtDate(run.pay_date)}
            </div>

            {/* Breakdown grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div>
                    <div style={labelStyle}>{t('base')}</div>
                    <div style={{ ...valueStyle, fontWeight: 600 }}>{fmtMoney(currency, lineItem.base_pay)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('overtime')}</div>
                    <div style={valueStyle}>{fmtMoney(currency, lineItem.overtime_pay)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('tips')}</div>
                    <div style={valueStyle}>{fmtMoney(currency, lineItem.tips_pay)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('bonus')}</div>
                    <div style={valueStyle}>{fmtMoney(currency, lineItem.bonus_pay)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('back_pay')}</div>
                    <div style={valueStyle}>{fmtMoney(currency, lineItem.back_pay)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('advance_deduction')}</div>
                    <div style={{ ...valueStyle, color: C.redText }}>-{fmtMoney(currency, lineItem.advance_deduction)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('allowance_deduction')}</div>
                    <div style={{ ...valueStyle, color: C.redText }}>-{fmtMoney(currency, lineItem.allowance_deduction)}</div>
                </div>
            </div>

            {/* Totals strip */}
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 14, marginBottom: 14 }}>
                <div>
                    <div style={labelStyle}>{t('gross')}</div>
                    <div style={{ ...valueStyle, fontWeight: 700 }}>{fmtMoney(currency, lineItem.gross_pay)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('net')}</div>
                    <div style={{ ...valueStyle, fontWeight: 800, color: C.text }}>{fmtMoney(currency, lineItem.net_pay)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('paid')}</div>
                    <div style={valueStyle}>{fmtMoney(currency, paidSum)}</div>
                </div>
                <div>
                    <div style={labelStyle}>{t('owed')}</div>
                    <div style={{ ...valueStyle, fontWeight: 700, color: outstanding > 0 ? C.redText : C.green }}>
                        {fmtMoney(currency, outstanding)}
                    </div>
                </div>
            </div>

            {/* Payments list */}
            {lineItem.payments?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                    <div style={labelStyle}>{t('payments')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {lineItem.payments.map(p => (
                            <div key={p.payment_id} style={{ fontSize: 12, color: C.textLight, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: C.bg, borderRadius: 6 }}>
                                <span>{fmtDate(p.payment_date)} · {t(METHOD_KEY[p.method] || 'method_other')}</span>
                                <span style={{ fontWeight: 600 }}>{fmtMoney(currency, p.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Inline actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => onBonusChanged('bonus')} style={secondaryButtonStyle}>{t('add_bonus')}</button>
                <button onClick={() => onBonusChanged('advance')} style={secondaryButtonStyle}>{t('add_advance')}</button>
            </div>
        </div>
    );
};

// ─── Add bonus modal ──────────────────────────────────────────────────────────

const AddBonusModal = ({ employeeId, onClose, onSaved, t }) => {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({ amount: '', effective_period: today, reason: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        if (!form.amount || parseFloat(form.amount) <= 0) return setError(t('amount_must_be_positive'));
        if (!form.reason.trim()) return setError(t('reason_is_required'));
        try {
            setSaving(true);
            setError('');
            await employeeV2Service.createBonus(employeeId, {
                employee_id: employeeId,
                amount: parseFloat(form.amount),
                effective_period: form.effective_period,
                reason: form.reason.trim(),
            });
            await onSaved();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.detail || t('failed_to_save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 440, padding: '24px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('add_bonus')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '8px 12px', fontSize: 12, color: C.redText, marginBottom: 12 }}>{error}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                    <div>
                        <label style={labelStyle}>{t('amount')} *</label>
                        <input style={inputStyle} type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                    </div>
                    <div>
                        <label style={labelStyle}>{t('effective_period')}</label>
                        <input style={inputStyle} type="date" value={form.effective_period} onChange={(e) => setForm({ ...form, effective_period: e.target.value })} />
                    </div>
                    <div>
                        <label style={labelStyle}>{t('reason')} *</label>
                        <input style={inputStyle} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={secondaryButtonStyle}>{t('cancel')}</button>
                    <button onClick={submit} disabled={saving} style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1, cursor: saving ? 'default' : 'pointer' }}>
                        {saving ? t('saving') : t('save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Add advance modal ────────────────────────────────────────────────────────

const AddAdvanceModal = ({ employeeId, onClose, onSaved, t }) => {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const timeStr = today.toTimeString().slice(0, 5);
    const [form, setForm] = useState({ amount: '', date: dateStr, time: timeStr, notes: '', source: 'external', session_id: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [activeSessions, setActiveSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(true);

    useEffect(() => {
        sessionService.getActiveSessions()
            .then((sessions) => setActiveSessions(sessions || []))
            .catch(() => setActiveSessions([]))
            .finally(() => setLoadingSessions(false));
    }, []);

    const submit = async () => {
        if (!form.amount || parseFloat(form.amount) <= 0) return setError(t('amount_must_be_positive'));
        if (form.source === 'register' && !form.session_id) return setError(t('select_register') || 'Select a register');
        try {
            setSaving(true);
            setError('');
            await employeeService.createSalaryAdvance({
                employee_id: employeeId,
                amount: parseFloat(form.amount),
                date: form.date,
                time: form.time,
                notes: form.notes || null,
                session_id: form.source === 'register' ? form.session_id : null,
            });
            await onSaved();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.detail || t('failed_to_save'));
        } finally {
            setSaving(false);
        }
    };

    const hasActiveBD = activeSessions.length > 0;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 14, width: 440, padding: '24px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{t('add_advance')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mutedLight, padding: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                {error && <div style={{ background: C.redLight, border: `1px solid ${C.redBorder}`, borderRadius: 7, padding: '8px 12px', fontSize: 12, color: C.redText, marginBottom: 12 }}>{error}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                    <div>
                        <label style={labelStyle}>{t('amount')} *</label>
                        <input style={inputStyle} type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                    </div>
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div>
                            <label style={labelStyle}>{t('date')}</label>
                            <input style={inputStyle} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('time')}</label>
                            <input style={inputStyle} type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
                        </div>
                    </div>

                    {/* Source selector — only if active business day */}
                    {!loadingSessions && hasActiveBD && (
                        <div>
                            <label style={labelStyle}>{t('source') || 'Source'}</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {[
                                    { key: 'register', label: t('from_register') || 'From Register' },
                                    { key: 'external', label: t('external') || 'External' },
                                ].map(opt => (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => setForm({ ...form, source: opt.key, session_id: '' })}
                                        style={{
                                            flex: 1, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                                            fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, fontWeight: 600,
                                            border: `1.5px solid ${form.source === opt.key ? '#2563eb' : '#e2e8f0'}`,
                                            background: form.source === opt.key ? '#eff5ff' : '#ffffff',
                                            color: form.source === opt.key ? '#2563eb' : '#64748b',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Register dropdown */}
                    {!loadingSessions && hasActiveBD && form.source === 'register' && (
                        <div>
                            <label style={labelStyle}>{t('select_register') || 'Select Register'} *</label>
                            <select
                                style={{ ...inputStyle, cursor: 'pointer' }}
                                value={form.session_id}
                                onChange={(e) => setForm({ ...form, session_id: e.target.value })}
                            >
                                <option value="">{t('select') || '-- Select --'}</option>
                                {activeSessions.map(s => (
                                    <option key={s.session_id} value={s.session_id}>
                                        {s.employee_name} — {s.sales_date}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label style={labelStyle}>{t('notes')}</label>
                        <input style={inputStyle} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t('optional')} />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={secondaryButtonStyle}>{t('cancel')}</button>
                    <button onClick={submit} disabled={saving} style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1, cursor: saving ? 'default' : 'pointer' }}>
                        {saving ? t('saving') : t('save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Pay history timeline (from payslips) ────────────────────────────────────

const PayHistoryCard = ({ employee, currency, t }) => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await employeeV2Service.listPayslips(employee.employee_id);
            setItems(data);
        } catch {
            /* empty state on error */
        } finally {
            setLoading(false);
        }
    }, [employee.employee_id]);

    useEffect(() => { load(); }, [load]);

    const openPayslip = async (payslipId) => {
        try {
            const payload = await payslipService.getPdfPayload(payslipId);
            openPayslipPrintWindow(payload, t, currency);
        } catch {
            // eslint-disable-next-line no-alert
            window.alert(t('failed_to_load'));
        }
    };

    return (
        <div style={cardStyle}>
            <div style={cardHeaderStyle}>
                <h3 style={cardTitleStyle}>{t('pay_history')}</h3>
            </div>

            {loading ? (
                <div style={{ fontSize: 13, color: C.mutedLight, padding: '12px 0' }}>{t('loading')}</div>
            ) : items.length === 0 ? (
                <div style={{ fontSize: 13, color: C.mutedLight, padding: '16px 0' }}>{t('no_pay_history')}</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {items.map(it => {
                        const s = it.snapshot_summary || {};
                        return (
                            <div key={it.payslip_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, gap: 10 }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                                        {fmtDate(s.period_start)} – {fmtDate(s.period_end)}
                                    </div>
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                        {t('net')}: {fmtMoney(currency, s.net_pay)} · {t('paid')}: {fmtMoney(currency, s.paid)}
                                        {Number(s.outstanding) > 0 && ` · ${t('owed')}: ${fmtMoney(currency, s.outstanding)}`}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                    <button onClick={() => openPayslip(it.payslip_id)} style={linkButtonStyle}>{t('download_payslip')}</button>
                                    {s.pay_run_id && (
                                        <button onClick={() => navigate(`/portal/payrun/${s.pay_run_id}`)} style={{ ...linkButtonStyle, color: C.muted }}>
                                            {t('view_run')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Print-window payslip renderer (matches the existing Reporting PDF pattern).
const openPayslipPrintWindow = (payload, t, currency) => {
    if (!payload) return;
    const snap = payload.snapshot_json || payload;
    const { tenant, employee: emp, pay_run: run, totals, earnings, deductions, payments, generated_at, generated_by } = snap;
    const header = { tenant_name: tenant?.name || '' };
    const win = window.open('', '_blank');
    if (!win) return;
    const row = (label, value) => `<tr><td style="padding:4px 8px;color:#64748b">${label}</td><td style="padding:4px 8px;text-align:right;font-weight:600">${value}</td></tr>`;
    const html = `
<!doctype html><html><head><meta charset="utf-8" />
<title>${t('payslip')} — ${emp?.name || ''}</title>
<style>
body{font-family:'Helvetica Neue',Arial,sans-serif;color:#212529;padding:32px;max-width:720px;margin:0 auto}
h1{font-size:20px;margin:0 0 4px}
h2{font-size:14px;color:#64748b;margin:0 0 24px;font-weight:500}
h3{font-size:13px;text-transform:uppercase;color:#64748b;letter-spacing:.8px;margin:18px 0 8px}
table{width:100%;border-collapse:collapse;font-size:13px}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #2563eb;padding-bottom:12px;margin-bottom:16px}
.meta{font-size:11px;color:#94a3b8}
.total{background:#f8fafc;padding:12px;border-radius:8px;margin-top:12px}
</style></head>
<body>
<div class="hdr"><div><h1>${header?.tenant_name || ''}</h1><h2>${t('payslip')}</h2></div><div class="meta">${fmtDateTime(generated_at)}<br/>${generated_by || ''}</div></div>
<h3>${t('employee')}</h3>
<table>
${row(t('name'), emp?.name || '')}
${row(t('role'), emp?.role || '')}
${row(t('department'), emp?.department || '—')}
</table>
<h3>${t('pay_period')}</h3>
<table>
${row(t('period'), `${fmtDate(run?.period_start)} – ${fmtDate(run?.period_end)}`)}
${row(t('pay_date'), fmtDate(run?.pay_date))}
${row(t('cadence'), run?.cadence || '')}
</table>
<h3>${t('earnings')}</h3>
<table>
${Object.entries(earnings || {}).map(([k, v]) => row(t(k) || k, fmtMoney(currency, v))).join('')}
</table>
<h3>${t('deductions')}</h3>
<table>
${Object.entries(deductions || {}).map(([k, v]) => row(t(k) || k, fmtMoney(currency, v))).join('')}
</table>
<div class="total"><table>
${row(`<strong>${t('gross')}</strong>`, `<strong>${fmtMoney(currency, totals?.gross_pay)}</strong>`)}
${row(`<strong>${t('net')}</strong>`, `<strong>${fmtMoney(currency, totals?.net_pay)}</strong>`)}
${row(t('paid'), fmtMoney(currency, totals?.paid))}
${row(t('owed'), fmtMoney(currency, totals?.outstanding))}
</table></div>
<h3>${t('payments')}</h3>
<table>
${(payments || []).map(p => row(`${fmtDate(p.payment_date)} · ${p.method}`, fmtMoney(currency, p.amount))).join('') || `<tr><td colspan="2" style="padding:8px;color:#94a3b8">${t('no_payments')}</td></tr>`}
</table>
<script>window.onload=()=>window.print();</script>
</body></html>`;
    win.document.open();
    win.document.write(html);
    win.document.close();
};

// ─── Ledger cards (advances / allowances / bonuses) ──────────────────────────

const LedgerCard = ({ title, loading, items, empty, renderItem, t }) => (
    <div style={cardStyle}>
        <div style={cardHeaderStyle}><h3 style={cardTitleStyle}>{title}</h3></div>
        {loading ? (
            <div style={{ fontSize: 13, color: C.mutedLight, padding: '12px 0' }}>{t('loading')}</div>
        ) : items.length === 0 ? (
            <div style={{ fontSize: 13, color: C.mutedLight, padding: '16px 0' }}>{empty}</div>
        ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map(renderItem)}
            </div>
        )}
    </div>
);

const LedgersSection = ({ employee, currency, refreshKey, t }) => {
    const [advances, setAdvances] = useState({ loading: true, items: [] });
    const [allowances, setAllowances] = useState({ loading: true, items: [] });
    const [bonuses, setBonuses] = useState({ loading: true, items: [] });

    const load = useCallback(async () => {
        setAdvances({ loading: true, items: [] });
        setAllowances({ loading: true, items: [] });
        setBonuses({ loading: true, items: [] });
        try {
            const [adv, alw, bns] = await Promise.all([
                employeeService.getEmployeeAdvances(employee.employee_id).catch(() => []),
                employeeService.getEmployeeAllowances(employee.employee_id).catch(() => []),
                employeeV2Service.listBonuses(employee.employee_id).catch(() => []),
            ]);
            setAdvances({ loading: false, items: adv || [] });
            setAllowances({ loading: false, items: alw || [] });
            setBonuses({ loading: false, items: bns || [] });
        } catch {
            setAdvances(s => ({ ...s, loading: false }));
            setAllowances(s => ({ ...s, loading: false }));
            setBonuses(s => ({ ...s, loading: false }));
        }
    }, [employee.employee_id]);

    useEffect(() => { load(); }, [load, refreshKey]);

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            <LedgerCard
                title={t('advances')} loading={advances.loading} items={advances.items}
                empty={t('no_advances')} t={t}
                renderItem={(a) => (
                    <div key={a.expense_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{fmtMoney(currency, a.amount)}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                {fmtDate(a.date)}{a.notes ? ` · ${a.notes}` : ''}
                            </div>
                        </div>
                    </div>
                )}
            />
            <LedgerCard
                title={t('allowances')} loading={allowances.loading} items={allowances.items}
                empty={t('no_allowances')} t={t}
                renderItem={(a) => (
                    <div key={a.staff_allowance_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{fmtMoney(currency, a.total_amount)}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                {fmtDate(a.date)} · {a.product_name} × {a.quantity}
                            </div>
                        </div>
                    </div>
                )}
            />
            <LedgerCard
                title={t('bonuses')} loading={bonuses.loading} items={bonuses.items}
                empty={t('no_bonuses')} t={t}
                renderItem={(b) => (
                    <div key={b.bonus_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{fmtMoney(currency, b.amount)}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                {fmtDate(b.effective_period)} · {b.reason}
                            </div>
                        </div>
                    </div>
                )}
            />
        </div>
    );
};

// ─── Main export ──────────────────────────────────────────────────────────────

const TabPayroll = ({ employee, currency, t }) => {
    const [modal, setModal] = useState(null); // 'bonus' | 'advance' | null
    const [refreshKey, setRefreshKey] = useState(0);

    const refresh = () => setRefreshKey(k => k + 1);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <CurrentPeriodCard
                key={`current-${refreshKey}`}
                employee={employee}
                currency={currency}
                onBonusChanged={setModal}
                t={t}
            />
            <PayHistoryCard employee={employee} currency={currency} t={t} />
            <LedgersSection employee={employee} currency={currency} refreshKey={refreshKey} t={t} />

            {modal === 'bonus' && (
                <AddBonusModal employeeId={employee.employee_id} onClose={() => setModal(null)} onSaved={refresh} t={t} />
            )}
            {modal === 'advance' && (
                <AddAdvanceModal employeeId={employee.employee_id} onClose={() => setModal(null)} onSaved={refresh} t={t} />
            )}
        </div>
    );
};

export default TabPayroll;
